import { FC, useState } from "react"
import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux";
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';

import { IconButtonRounded, NgPageTitle } from "../UI"
import { addDismissedSection, addNotification, ngAppSelectors, toggleSectionExpanded } from "../../store";

interface Props {
    title: string
    sectionKey: string
    maxWidth?: string
    dismissable?: boolean
    secondary?: boolean
    headerSlot?: any;
    children?: React.ReactNode;
}

const StyledContainer = styled.section<{ $maxWidth?: string }>`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.medium};

    .content {
        max-width: ${({ $maxWidth }) => $maxWidth ? $maxWidth : 'inherit'};
    }

    .expand-icon {
        cursor: pointer;
        
        margin-top: 0.4rem;

        svg {
            font-size: 2rem;
            opacity: 0.3;
        }
    }

    .header {
        display: flex;
        justify-content: space-between;
        gap: ${({ theme }) => theme.spacing.xLarge};
    }

    .header-section {
        display: flex;
    }

    .header-slot {
        flex: 1;             
        display: flex;       
        justify-content: flex-end;
        align-items: center;
    }

    .header-title {
        cursor: pointer;
        display: flex;
        gap: ${({ theme }) => theme.spacing.small};
    }

    p {
        font-size: ${({ theme }) => theme.fontSize.default};
        line-height: ${({ theme }) => theme.spacing.large};
    }

     @media (max-width: 900px) {
        .header {
            flex-direction: column;
            align-items: stretch;
            gap: ${({ theme }) => theme.spacing.small};
        }

        .header-section,
        .header-slot {
            width: 100%;
            justify-content: space-between;
        }

        .header-slot {
            justify-content: flex-start;
        }
    }
`

export const NgPageSection: FC<Props> = ({ title, sectionKey, maxWidth, dismissable, secondary, children, headerSlot, }) => {
    const dispatch = useDispatch()

    const isDismissed = useSelector((state) => ngAppSelectors.isSectionDismissed(state, sectionKey));
    const isExpanded = useSelector((state) => ngAppSelectors.isSectionExpanded(state, sectionKey));

    const toggleExpanded = () => {
        dispatch(toggleSectionExpanded(sectionKey));
    }

    const handleDismiss = () => {
        dispatch(addDismissedSection(sectionKey));

        dispatch(addNotification({
            type: 'success',
            message: `Section "${title}" has been dismissed.`
        }));
    }

    if (isDismissed) {
        return null;
    }

    return (
        <StyledContainer $maxWidth={maxWidth}>
            <div className="header">
                <div className="header-section">
                    <div className="header-title" onClick={toggleExpanded}>
                        <div className="expand-icon">
                            {isExpanded ? <ExpandMoreOutlinedIcon /> : <ChevronRightOutlinedIcon />}
                        </div>
                        <NgPageTitle title={title} secondary={secondary} />
                    </div>

                    {dismissable && <IconButtonRounded
                        title="Dismiss"
                        icon={<CloseIcon />}
                        onClick={handleDismiss}
                        activeAccent
                        flipIcon />}
                </div>

                {
                    headerSlot &&
                    <div className="header-slot">
                        {headerSlot}
                    </div>
                }

            </div>

            {
                isExpanded && (<div className="content">
                    {children}
                </div>)
            }
        </StyledContainer>
    )
}